'use client'

import { useEffect, useRef } from 'react'

import type { SelectedEmailInfo } from '@/components/canvas/selected-email-chips'
import {
  clearPublicComposerDraftStorage,
  readPublicComposerDraft,
} from '@/components/chat/public-composer-draft'
import type { GlobalChatContextValue } from '@/components/global-chat/global-chat-context'
import { useGlobalChatSafe } from '@/components/global-chat/global-chat-context'
import { useOptionalBrandContext } from '@/contexts/brand-context'

export type TemplateChatAttachProps = {
  emailId: string
  title: string
  imagePngUrl: string | null
  /**
   * Set by the flow page (`FlowChatAttach`): the chip stands for a public
   * flow template, `emailId` is its synthetic `flow:<slug>` key, and a
   * signed-out draft is restored only when it was written for THIS flow.
   */
  remixSourceFlowSlug?: string
  /** The line under the chip's title ("By Hypermatic", "5 emails in 14 days"). */
  subtitle?: string
  /** A flow's first step cards, fanned out on the chip. */
  thumbnails?: ReadonlyArray<string>
}

/**
 * One template-page visit's claim on the side chat. Reset whenever
 * `emailId` changes so navigating template → template starts a clean
 * claim instead of inheriting the previous template's bookkeeping.
 */
type TemplateChatVisit = {
  emailId: string
  /** The chat this visit opened (or adopted) for the remix. */
  claimedChatId: string
  /** True once `activeChatId` has caught up with `claimedChatId`. */
  hasLandedOnClaim: boolean
  /**
   * Every chat this visit stamped → whether the chip was ALREADY
   * attached there beforehand (so the leave-cleanup restores it instead
   * of deleting an attachment the user made from the canvas).
   */
  stamped: Map<string, boolean>
}

/**
 * Headless template-page → global-chat bridge. The template chip is scoped
 * to the PAGE, not to the conversation: it exists for exactly as long as
 * the user is looking at `/templates/email/<id>`.
 *
 *   - **Landing** opens a NEW chat and stages the template there. A chat
 *     the user has never submitted in already IS a new chat, so it is
 *     reused rather than stacking an empty duplicate beside it; anything
 *     else (a real conversation, mid-stream or not) gets a fresh tab so a
 *     remix never lands in the middle of an unrelated thread.
 *   - **A collapsed panel is expanded** so the staged chip is visible.
 *   - **Switching chats while the template is on screen** re-stages the
 *     chip on the newly active chat — the attachment follows the user.
 *   - **At most one template per chat**: staging drops any other
 *     `isRemixSource` chip, so template → template swaps rather than
 *     accumulates.
 *   - **Leaving** (navigating away, or on to another template) detaches
 *     the chip from every chat this visit stamped.
 *
 * The chip's `isRemixSource` flag makes the thread forward
 * `remixSourceEmailId` to the orchestrator (REMIX MODE: rebrand for the
 * active brand; empty submits allowed) — see `CanvasChatThread`. A flow
 * page attaches through the same lifecycle with `remixSourceFlowSlug` set:
 * the chip then stands for the whole flow (REMIX FLOW MODE) and never
 * counts as a selected email.
 *
 * Also finishes the signed-out hand-off: a draft written by
 * `PublicRemixPromptBox` before the Clerk sign-in redirect is restored
 * into the claimed chat's composer FOR REVIEW (not auto-submitted) via the
 * global-chat composer seed. Draft FILES are dropped — re-staging them
 * would mean rebuilding `File`s into the chat's uploads store
 * (`composerUploads`); the restored text + chip carry the intent.
 */
export function TemplateChatAttach({
  emailId,
  title,
  imagePngUrl,
  remixSourceFlowSlug,
  subtitle,
  thumbnails,
}: TemplateChatAttachProps) {
  const ctx = useGlobalChatSafe()
  const activeChatId = ctx?.chatState.activeChatId ?? null

  // Wait for the brand list to settle before acting: `ctx.brandId` is the
  // attached-emails lattice's storage scope, so staging while it is still
  // resolving would write the chip under a scope the composer never
  // re-reads. After settle, an org with no brands keeps brandId undefined
  // consistently on both sides.
  const brandCtx = useOptionalBrandContext()
  const isBrandLoading = brandCtx ? brandCtx.isLoading : false

  const visitRef = useRef<TemplateChatVisit | null>(null)

  // Latest context for the unmount cleanup. Held in a ref (not a dep) so
  // the cleanup effect below runs ONLY on leave — `ctx` changes identity
  // on nearly every provider render, and depending on it would tear down
  // and re-stage the chip continuously.
  const ctxRef = useRef<GlobalChatContextValue | null>(ctx)
  useEffect(() => {
    ctxRef.current = ctx
  })

  // ── Claim a chat for this template ──────────────────────────────────
  useEffect(() => {
    // Chat actions restore their scope atomically before changing it, so a
    // descendant can claim its chat before the provider's restoration effect.
    if (!ctx) {
      return
    }
    if (isBrandLoading) {
      return
    }
    // One claim per template. Survives a StrictMode remount cycle (the
    // leave-cleanup clears `stamped` but keeps the visit) so the dev
    // double-invoke can never open two tabs.
    if (visitRef.current?.emailId === emailId) {
      return
    }

    const { chatState } = ctx
    const currentChatId = chatState.activeChatId
    const currentTab = currentChatId
      ? chatState.openTabs.find((t) => t.chatId === currentChatId)
      : undefined
    // `isNew` is cleared on a chat's first submit (`markTabActive`) and
    // fresh tabs are never persisted, so it means exactly "empty chat the
    // user has not started" — reusable as the remix's new chat.
    const claimedChatId =
      currentChatId && currentTab?.isNew ? currentChatId : chatState.addTab()

    // Self-heal: a visit that ended by hard reload / tab close ran no
    // React teardown, so its chip is still sitting in some chat. Only the
    // templates page ever sets the flag and only while mounted, so every
    // flagged chip on the strip right now is a leftover. (The claimed chat
    // is swept too — `setChatRemixSource` re-stages it a beat later.)
    // biome-ignore lint/suspicious/useIterableCallbackReturn: pre-Ultracite baseline — verify callback return intent when touching
    chatState.openTabs.forEach((tab) =>
      ctx.clearAllChatRemixSources(tab.chatId)
    )

    visitRef.current = {
      emailId,
      claimedChatId,
      hasLandedOnClaim: claimedChatId === currentChatId,
      stamped: new Map(),
    }

    const draft = readPublicComposerDraft()
    if (draft) {
      // One global slot: a draft belongs to THIS page only when it was
      // written for the same thing — the template page takes an unflagged
      // draft, a flow page the draft written for its own flow. Anything
      // else is cleared unseeded.
      const draftFlowSlug = draft.remixSourceFlowSlug ?? null
      if (draft.text && draftFlowSlug === (remixSourceFlowSlug ?? null)) {
        ctx.seedComposer(claimedChatId, draft.text)
      }
      void clearPublicComposerDraftStorage(draft.filesKey)
    }

    ctx.ensurePanelExpanded()
  }, [ctx, emailId, isBrandLoading, remixSourceFlowSlug])

  // ── Keep the chip on whatever chat is active ────────────────────────
  useEffect(() => {
    if (!ctx) {
      return
    }
    if (!activeChatId) {
      return
    }
    const visit = visitRef.current
    if (!visit || visit.emailId !== emailId) {
      return
    }

    if (!visit.hasLandedOnClaim) {
      // `addTab()`'s state update lands a render later — ignore the stale
      // pre-claim active id so the chip never touches the chat the user
      // arrived from.
      if (activeChatId !== visit.claimedChatId) {
        return
      }
      visit.hasLandedOnClaim = true
    }

    // Once per chat: re-running would resurrect the chip after a submit
    // cleared it, which would re-arm REMIX MODE on the user's next turn.
    if (visit.stamped.has(activeChatId)) {
      return
    }

    const chip: SelectedEmailInfo = {
      emailId,
      title,
      ...(imagePngUrl !== null ? { imagePngUrl } : {}),
      isRemixSource: true,
      ...(remixSourceFlowSlug ? { remixSourceFlowSlug } : {}),
      ...(subtitle ? { subtitle } : {}),
      ...(thumbnails && thumbnails.length > 0 ? { thumbnails } : {}),
    }
    visit.stamped.set(activeChatId, ctx.setChatRemixSource(activeChatId, chip))
  }, [
    ctx,
    activeChatId,
    emailId,
    title,
    imagePngUrl,
    remixSourceFlowSlug,
    subtitle,
    thumbnails,
  ])

  // ── Detach on leave ─────────────────────────────────────────────────
  useEffect(() => {
    return () => {
      const visit = visitRef.current
      if (!visit || visit.emailId !== emailId) {
        return
      }
      const chat = ctxRef.current
      visit.stamped.forEach((wasChipPreexisting, chatId) => {
        chat?.clearChatRemixSource(chatId, emailId, wasChipPreexisting)
      })
      // Keep the visit (minus its stamps) so a StrictMode remount does not
      // re-claim a second tab — clearing the stamps is what lets the
      // re-run re-stage the chip. A real navigation destroys the ref with
      // the component.
      visit.stamped.clear()
    }
  }, [emailId])

  return null
}
